import { useMemo, useState } from 'react';
import { toMajor } from '../../contracts/money';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Search,
  ShieldCheck,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Banner } from '../../components/Banner';
import { Drawer } from '../../components/Drawer';
import { Input } from '../../components/Input';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { cn } from '../../lib/cn';
import { resolveFloor } from '../../api/admin';
import type {
  PlatformPricingTier,
  PlatformPricingTierSlug,
} from '../../mocks/admin';
import {
  useCurrentReseller,
  useResellerPackages,
  useResellerPricingTiers,
  addResellerPackage,
  removeResellerPackage,
  updateResellerPackage,
} from '../../api/reseller';
import { usePackageCategories } from '../../api/catalog';
import { formatInt } from '../../lib/format';
import { useQueryClient } from '@tanstack/react-query';

const STATUS_TONE = {
  published: 'success' as const,
  draft: 'info' as const,
  deprecated: 'warning' as const,
  archived: 'neutral' as const,
};

const STATUS_LABEL = {
  published: 'Published',
  draft: 'Draft',
  deprecated: 'Deprecated',
  archived: 'Archived',
};

/** Look up a tier's display name by slug. Falls back to the raw slug if missing. */
function tierDisplayName(slug: PlatformPricingTierSlug, tiers: PlatformPricingTier[]): string {
  return tiers.find((t) => t.slug === slug)?.displayName ?? slug;
}

/** Build the resource hint string the drawer shows under the tier picker. */
function tierHintString(tier: PlatformPricingTier): string {
  const parts: string[] = [];
  parts.push(tier.hintMaxLocations === null ? 'unlimited loc' : `${tier.hintMaxLocations} loc`);
  parts.push(tier.hintMaxUsers === null ? 'unlimited usr' : `${tier.hintMaxUsers} usr`);
  parts.push(
    tier.hintMaxProducts === null
      ? 'unlimited products'
      : `${formatInt(tier.hintMaxProducts)} products`,
  );
  return parts.join(', ');
}

/**
 * Reseller's own packages — `/reseller/packages`.
 *
 * Every reseller defines their own catalog with their own prices. The platform
 * takes a variable commission cut (set per-reseller by the platform admin).
 *
 * Scoping: this page only shows the active reseller's packages. Reseller A
 * cannot see Reseller B's catalog — enforced at the API by `WHERE reseller_id
 * = $jwt.reseller_id` and at the database by Postgres RLS.
 */
export function PackagesStrategy() {
  const qc = useQueryClient();
  const { data: currentReseller } = useCurrentReseller();
  const { data: resellerPackages = [] } = useResellerPackages();
  const { data: platformPricingTiers = [] } = useResellerPricingTiers();
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft' | 'deprecated' | 'archived'>('all');
  const [search, setSearch] = useState('');
  const [newPkgOpen, setNewPkgOpen] = useState(false);
  const [editingPkg, setEditingPkg] = useState<(typeof resellerPackages)[number] | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const myPackages = currentReseller ? resellerPackages.filter((p) => p.resellerId === currentReseller.id) : [];

  const filtered = useMemo(() => {
    return myPackages.filter((p) => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && !p.internalCode.includes(search.toLowerCase())) return false;
      return true;
    });
  }, [myPackages, statusFilter, search]);

  const totalSubs = myPackages.reduce((s, p) => s + p.subscriberCount, 0);

  // The reseller record (commissionPct, slug) comes straight from /reseller/me.
  const r = currentReseller
    ? { id: currentReseller.id, slug: currentReseller.slug, commissionPct: currentReseller.commissionPct }
    : undefined;

  if (!currentReseller || !r) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-[1440px] mx-auto space-y-4" aria-busy="true">
        <div className="skeleton h-8 w-64 max-w-full rounded" />
        <div className="space-y-3">
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
        </div>
      </div>
    );
  }

  async function deletePackage(id: string, name: string) {
    setDeleteError(null);
    try {
      await removeResellerPackage(id);
      void qc.invalidateQueries({ queryKey: ['reseller', 'packages'] });
    } catch (e) {
      const code = (e as { status?: number })?.status;
      setDeleteError(
        code === 409
          ? `"${name}" has active subscribers — deprecate it instead of deleting.`
          : `Could not delete "${name}".`,
      );
    }
  }

  async function toggleStatus(p: { id: string; status: string; name: string }) {
    setDeleteError(null);
    const next = p.status === 'published' ? 'deprecated' : 'published';
    try {
      await updateResellerPackage(p.id, { status: next });
      void qc.invalidateQueries({ queryKey: ['reseller', 'packages'] });
    } catch {
      setDeleteError(`Could not ${next === 'published' ? 'activate' : 'deactivate'} "${p.name}".`);
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Pricing"
        title={
          <span className="flex items-center gap-3 flex-wrap">
            My packages
            <span className="text-base font-normal text-zinc-500 dark:text-zinc-400 tabular-nums">
              ({myPackages.length})
            </span>
          </span>
        }
        subtitle={`Your own catalog. Customers buy at your prices; the platform takes a ${r.commissionPct}% commission cut on every payment.`}
        action={
          <Button iconLeft={<Plus className="w-4 h-4" />} onClick={() => setNewPkgOpen(true)}>
            New package
          </Button>
        }
      />

      {newPkgOpen && <NewPackageDrawer open onClose={() => setNewPkgOpen(false)} reseller={r} />}
      {editingPkg && <NewPackageDrawer open onClose={() => setEditingPkg(null)} reseller={r} editing={editingPkg} />}

      {/* Commission disclosure */}
      <Banner tone="info" className="mb-5" title="How your pricing works">
        <p>
          You set your own monthly + yearly prices on each package. When a customer pays you{' '}
          <strong>৳1,000</strong>, the platform takes{' '}
          <strong className="tabular-nums">{r.commissionPct}%</strong> (
          <Money minor={Math.round(1000 * (r.commissionPct / 100))} hideCents tone="neutral" /> commission)
          and you receive <Money minor={Math.round(1000 * (1 - r.commissionPct / 100))} hideCents tone="neutral" />{' '}
          on the next payout. Your commission rate is set by ITCity and is the same across all packages.
        </p>
      </Banner>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <StatTile label="Total packages" value={myPackages.length.toString()} subtitle={`${myPackages.filter((p) => p.status === 'published').length} published`} />
        <StatTile label="Subscribers" value={formatInt(totalSubs)} subtitle="across all packages" />
        <StatTile
          label="Cheapest"
          value={
            <Money
              minor={Math.min(...(myPackages.length ? myPackages.map((p) => p.monthlyMinor) : [0]))}
              hideCents
              tone="neutral"
              className="text-zinc-950 dark:text-zinc-50"
            />
          }
          subtitle="/ month"
        />
        <StatTile
          label="Highest"
          value={
            <Money
              minor={Math.max(...(myPackages.length ? myPackages.map((p) => p.monthlyMinor) : [0]))}
              hideCents
              tone="neutral"
              className="text-zinc-950 dark:text-zinc-50"
            />
          }
          subtitle="/ month"
        />
      </div>

      {deleteError && (
        <div className="mb-4 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">
          {deleteError}
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-2 border-b border-zinc-200 dark:border-zinc-700">
          <FilterDropdown
            label="Status"
            value={statusFilter === 'all' ? 'All' : STATUS_LABEL[statusFilter]}
            options={[
              { v: 'all', l: 'All' },
              { v: 'published', l: 'Published' },
              { v: 'draft', l: 'Draft' },
              { v: 'deprecated', l: 'Deprecated' },
              { v: 'archived', l: 'Archived' },
            ]}
            onChange={(v) => setStatusFilter(v as typeof statusFilter)}
          />
          <div className="ml-auto w-full sm:w-72">
            <Input
              placeholder="Search packages…"
              prefix={<Search className="w-4 h-4" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <Sparkles className="w-8 h-8 text-zinc-400 dark:text-zinc-500 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
              {myPackages.length === 0 ? 'No packages yet' : 'No matches'}
            </h3>
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400 max-w-md mx-auto">
              {myPackages.length === 0
                ? 'Create your first package — pick a name, set monthly + yearly prices, and customers can subscribe at your subdomain.'
                : 'Try a different filter or search term.'}
            </p>
            {myPackages.length === 0 && (
              <Button className="mt-5" iconLeft={<Plus className="w-4 h-4" />} onClick={() => setNewPkgOpen(true)}>
                Create first package
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto nice-scroll">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
                <tr>
                  <th className="text-left font-medium px-5 sm:px-6 py-3">Package</th>
                  <th className="text-left font-medium px-4 py-3">Tier</th>
                  <th className="text-right font-medium px-4 py-3">Monthly</th>
                  <th className="text-right font-medium px-4 py-3 hidden md:table-cell">Yearly</th>
                  <th className="text-right font-medium px-4 py-3 hidden lg:table-cell">You receive</th>
                  <th className="text-right font-medium px-4 py-3">Subscribers</th>
                  <th className="text-left font-medium px-4 py-3">Status</th>
                  <th className="px-5 sm:px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
                {filtered.map((p) => {
                  const floor = resolveFloor(currentReseller.id, p.tierSlug);
                  const grandfathered = p.monthlyMinor < floor.monthly;
                  return (
                  <tr key={p.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                    <td className="px-5 sm:px-6 py-4">
                      <div className="font-medium text-zinc-950 dark:text-zinc-50">{p.name}</div>
                      <div className="text-xs text-zinc-500 dark:text-zinc-400">{p.tagline}</div>
                      <div className="text-[11px] text-zinc-400 dark:text-zinc-500 mono mt-0.5">{p.internalCode}</div>
                    </td>
                    <td className="px-4 py-4">
                      <Badge tone="neutral">{tierDisplayName(p.tierSlug, platformPricingTiers)}</Badge>
                    </td>
                    <td className="px-4 py-4 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1.5 justify-end">
                        {grandfathered && (
                          <span
                            title={`Grandfathered — below current ${tierDisplayName(p.tierSlug, platformPricingTiers)} floor of ৳${formatInt(floor.monthly)}/mo`}
                            className="inline-flex items-center text-amber-500 dark:text-amber-400"
                            aria-label="Grandfathered below current tier floor"
                          >
                            <AlertTriangle className="w-3.5 h-3.5" />
                          </span>
                        )}
                        <Money minor={p.monthlyMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50 font-medium" />
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right whitespace-nowrap hidden md:table-cell">
                      <Money minor={p.yearlyMinor} hideCents tone="neutral" className="text-zinc-700 dark:text-zinc-200" />
                    </td>
                    <td className="px-4 py-4 text-right whitespace-nowrap hidden lg:table-cell">
                      <Money
                        minor={Math.round(p.monthlyMinor * (1 - r.commissionPct / 100))}
                        hideCents
                        tone="neutral"
                        className="text-emerald-700 dark:text-emerald-300 font-medium"
                      />
                      <div className="text-[11px] text-zinc-500 dark:text-zinc-400">/ month after commission</div>
                    </td>
                    <td className="px-4 py-4 text-right tabular-nums text-zinc-700 dark:text-zinc-200">{p.subscriberCount}</td>
                    <td className="px-4 py-4">
                      <Badge tone={STATUS_TONE[p.status]}>{STATUS_LABEL[p.status]}</Badge>
                    </td>
                    <td className="px-5 sm:px-6 py-4 text-right">
                      <div className="inline-flex items-center gap-1 justify-end">
                        <button
                          type="button"
                          onClick={() => setEditingPkg(p)}
                          className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-zinc-800 dark:hover:text-zinc-100 transition"
                          title={`Edit ${p.name}`}
                          aria-label={`Edit ${p.name}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleStatus(p)}
                          className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-zinc-800 dark:hover:text-zinc-100 transition"
                          title={p.status === 'published' ? `Deactivate ${p.name}` : `Activate ${p.name}`}
                          aria-label={p.status === 'published' ? `Deactivate ${p.name}` : `Activate ${p.name}`}
                        >
                          {p.status === 'published' ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                        </button>
                        <button
                          type="button"
                          onClick={() => deletePackage(p.id, p.name)}
                          className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/40 dark:hover:text-rose-400 transition"
                          title={`Delete ${p.name}`}
                          aria-label={`Delete ${p.name}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Scoping reminder */}
      <p className="mt-4 text-xs text-zinc-500 dark:text-zinc-400 inline-flex items-center gap-1.5">
        <ShieldCheck className="w-3 h-3" />
        Only your packages are shown. Other resellers' catalogs are isolated by Row-Level Security.
      </p>
    </div>
  );
}

/* ── New package drawer (reseller-scoped) ─────────────────────────────── */

function NewPackageDrawer({
  open,
  onClose,
  reseller: r,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  reseller: { id: string; slug: string; commissionPct: number };
  editing?: {
    id: string;
    name: string;
    tagline?: string;
    tierSlug: string;
    monthlyMinor: number;
    yearlyMinor: number;
    status: string;
    features: { locations: number | 'unlimited'; users: number | 'unlimited'; products: number | 'unlimited'; invoices: number | 'unlimited'; modules: string[] };
  } | null;
}) {
  const qc = useQueryClient();
  const { data: packageCategories = [] } = usePackageCategories();
  const { data: platformPricingTiers = [] } = useResellerPricingTiers();
  const numStr = (v: number | 'unlimited') => (v === 'unlimited' ? '' : String(v));
  const [name, setName] = useState(editing?.name ?? '');
  const [tagline, setTagline] = useState(editing?.tagline ?? '');
  const [categoryId, setCategoryId] = useState('cat_pos');
  const [tierSlug, setTierSlug] = useState<PlatformPricingTierSlug | ''>((editing?.tierSlug as PlatformPricingTierSlug) ?? '');
  const [monthly, setMonthly] = useState(editing ? String(editing.monthlyMinor / 100) : '');
  const [autoYearly, setAutoYearly] = useState(!editing);
  const [yearly, setYearly] = useState(editing ? String(editing.yearlyMinor / 100) : '');
  const [locations, setLocations] = useState(editing ? numStr(editing.features.locations) || '1' : '1');
  const [users, setUsers] = useState(editing ? numStr(editing.features.users) || '1' : '3');
  const [products, setProducts] = useState(editing ? numStr(editing.features.products) || '1000' : '1000');
  const [invoices, setInvoices] = useState(editing ? numStr(editing.features.invoices) || '1000' : '1000');
  const [unlimitedProducts, setUnlimitedProducts] = useState(editing ? editing.features.products === 'unlimited' : false);
  const [unlimitedInvoices, setUnlimitedInvoices] = useState(editing ? editing.features.invoices === 'unlimited' : false);
  const [status, setStatus] = useState<'draft' | 'published'>(editing?.status === 'published' ? 'published' : 'draft');
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeCategories = useMemo(
    () => packageCategories.filter((c) => c.isActive).sort((a, b) => a.sortOrder - b.sortOrder),
    [packageCategories],
  );

  // Production: filter platformPricingTiers to those linked to `categoryId`
  // (a future `tier.categoryId` column). For the mock, every category sees
  // all active tiers since the linkage isn't wired yet.
  const activeTiers = useMemo(
    () => platformPricingTiers.filter((t) => t.isActive).sort((a, b) => a.sortOrder - b.sortOrder),
    [platformPricingTiers],
  );

  const selectedTier = useMemo(
    () => (tierSlug ? activeTiers.find((t) => t.slug === tierSlug) ?? null : null),
    [tierSlug, activeTiers],
  );

  // Effective floor for the picked tier (override-aware, scoped to this reseller).
  const floor = useMemo(
    () => (tierSlug ? resolveFloor(r.id, tierSlug) : null),
    [tierSlug, r.id],
  );

  const monthlyNum = Number(monthly) || 0;
  const computedYearly = autoYearly ? String(monthlyNum * 12) : yearly;
  const yearlyNum = Number(autoYearly ? computedYearly : yearly) || 0;
  const platformCut = Math.round(monthlyNum * (r.commissionPct / 100));
  const yourEarnings = monthlyNum - platformCut;

  // Live floor checks (floors are minor units; price inputs are taka — compare in
  // minor). Yearly only validates when the user has typed a value.
  const monthlyBelowFloor = floor !== null && monthlyNum > 0 && monthlyNum * 100 < floor.monthly;
  const yearlyBelowFloor = floor !== null && yearlyNum > 0 && yearlyNum * 100 < floor.yearly;

  const valid =
    name.trim().length > 0 &&
    tierSlug !== '' &&
    monthlyNum > 0 &&
    !monthlyBelowFloor &&
    !yearlyBelowFloor;

  function reset() {
    setName('');
    setTagline('');
    setCategoryId('cat_pos');
    setTierSlug('');
    setMonthly('');
    setYearly('');
    setLocations('1');
    setUsers('3');
    setProducts('1000');
    setInvoices('1000');
    setUnlimitedProducts(false);
    setUnlimitedInvoices(false);
    setStatus('draft');
    setAutoYearly(true);
    setSaved(false);
  }

  function close() {
    onClose();
    setTimeout(reset, 250);
  }

  async function save() {
    if (!valid) return; // `valid` already requires a non-empty tierSlug (narrows it below)
    setSubmitting(true);
    setError(null);
    try {
      const features = {
        locations: Number(locations) || 1,
        users: Number(users) || 1,
        products: unlimitedProducts ? ('unlimited' as const) : Number(products) || 0,
        invoices: unlimitedInvoices ? ('unlimited' as const) : Number(invoices) || 0,
        modules: [] as string[],
      };
      if (editing) {
        await updateResellerPackage(editing.id, {
          name: name.trim(),
          tagline: tagline.trim() || undefined,
          tierSlug,
          monthlyMinor: Math.round(monthlyNum * 100),
          yearlyMinor: Math.round(yearlyNum * 100),
          status,
          features,
        });
      } else {
        await addResellerPackage({
          name: name.trim(),
          internalCode: name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          tagline: tagline.trim() || undefined,
          tierSlug,
          monthlyMinor: Math.round(monthlyNum * 100),
          yearlyMinor: Math.round(yearlyNum * 100),
          status,
          features,
        });
      }
      void qc.invalidateQueries({ queryKey: ['reseller', 'packages'] });
      setSaved(true);
    } catch (e) {
      const code = (e as { status?: number })?.status;
      setError(code === 400 ? 'Check the price floors and required fields.' : editing ? 'Could not save the package.' : 'Could not create the package.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={close}
      title={saved ? (editing ? 'Package updated' : 'Package created') : editing ? 'Edit package' : 'New package'}
      width="lg"
      footer={
        saved ? (
          <Button onClick={close}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>Cancel</Button>
            <Button onClick={save} loading={submitting} disabled={!valid}>
              {editing ? 'Save changes' : status === 'published' ? 'Publish package' : 'Save draft'}
            </Button>
          </>
        )
      }
    >
      {saved ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-950/40 grid place-items-center text-emerald-700 dark:text-emerald-300 mb-4">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">{name} {editing ? 'updated' : 'created'}</h3>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            {editing ? (
              <>Changes saved in {status === 'published' ? 'published' : 'draft'} state.</>
            ) : (
              <>Saved as <span className="mono">{name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}</span> in {status === 'published' ? 'published' : 'draft'} state.</>
            )}
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {error && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <Input
            label="Name"
            required
            placeholder="Acme Growth · Sundarban Premium · Eid Special"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Input
            label="Tagline"
            placeholder="Most popular · For 2-3 outlets · Eid limited"
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
          />

          {/* ── Category picker ─────────────────────────────────────────────
            * Picks the vertical (POS Package / School / Internet / …). The
            * category drives whether signup provisions a Laravel POS DB or
            * is billing-only.
            *
            * NOTE: production would filter the tier list below by
            * category_id; for the mock we show all active tiers regardless.
            */}
          <div>
            <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
              Category
              <span className="text-rose-600 ml-0.5">*</span>
            </label>
            <div className="relative">
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className={cn(
                  'block w-full appearance-none px-3 py-2.5 pr-9 text-sm transition rounded-md',
                  'bg-white text-zinc-950',
                  'border border-zinc-300',
                  'focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand',
                  'dark:bg-zinc-900 dark:text-zinc-50 dark:border-zinc-700',
                  'dark:focus:ring-brand-dm dark:focus:border-brand-dm',
                )}
              >
                {activeCategories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName}
                    {c.provisionsPos ? ' · provisions POS' : ' · billing-only'}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            </div>
          </div>

          {/* ── Tier picker ─────────────────────────────────────────────────
            * Maps the package to a platform pricing tier. The tier drives
            * the price floor (with optional per-reseller override) that the
            * monthly/yearly inputs are validated against in real time.
            */}
          <div>
            <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
              Tier
              <span className="text-rose-600 ml-0.5">*</span>
            </label>
            <div className="relative">
              <select
                value={tierSlug}
                onChange={(e) => setTierSlug(e.target.value as PlatformPricingTierSlug | '')}
                className={cn(
                  'block w-full appearance-none px-3 py-2.5 pr-9 text-sm transition rounded-md',
                  'bg-white text-zinc-950',
                  'border border-zinc-300',
                  'focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand',
                  'dark:bg-zinc-900 dark:text-zinc-50 dark:border-zinc-700',
                  'dark:focus:ring-brand-dm dark:focus:border-brand-dm',
                )}
              >
                <option value="">Select a tier…</option>
                {activeTiers.map((t) => (
                  <option key={t.slug} value={t.slug}>
                    {t.displayName} · Min ৳{formatInt(toMajor(t.defaultMinMonthlyMinor))}/mo
                  </option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            </div>
            {selectedTier && floor && (
              <p className="mt-1.5 text-xs text-zinc-600 dark:text-zinc-300">
                Min ৳{formatInt(toMajor(floor.monthly))}/mo · Min ৳{formatInt(toMajor(floor.yearly))}/yr (
                {tierHintString(selectedTier)})
                {floor.source === 'override' && (
                  <span className="text-zinc-500 dark:text-zinc-400">
                    {' '}
                    (your reseller override)
                  </span>
                )}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Input
                label="Monthly price (BDT)"
                required
                type="number"
                min={0}
                placeholder="500"
                value={monthly}
                onChange={(e) => setMonthly(e.target.value)}
                prefix={<span className="text-sm">৳</span>}
                className={monthlyBelowFloor ? 'border-rose-500 focus:ring-rose-500 focus:border-rose-500 dark:border-rose-500' : ''}
              />
              {floor && monthlyNum > 0 && (
                <div className="mt-1.5 text-xs">
                  {monthlyBelowFloor ? (
                    <span className="text-rose-600 dark:text-rose-400">
                      ✗ Below tier floor (min ৳{formatInt(toMajor(floor.monthly))}/mo)
                    </span>
                  ) : (
                    <span className="text-emerald-600 dark:text-emerald-400/80">
                      ✓ above floor
                    </span>
                  )}
                </div>
              )}
            </div>
            <div>
              <Input
                label="Yearly price (BDT)"
                type="number"
                min={0}
                value={autoYearly ? computedYearly : yearly}
                onChange={(e) => setYearly(e.target.value)}
                disabled={autoYearly}
                prefix={<span className="text-sm">৳</span>}
                className={yearlyBelowFloor ? 'border-rose-500 focus:ring-rose-500 focus:border-rose-500 dark:border-rose-500' : ''}
              />
              {floor && yearlyNum > 0 && (
                <div className="mt-1.5 text-xs">
                  {yearlyBelowFloor ? (
                    <span className="text-rose-600 dark:text-rose-400">
                      ✗ Below tier floor (min ৳{formatInt(toMajor(floor.yearly))}/yr)
                    </span>
                  ) : (
                    <span className="text-emerald-600 dark:text-emerald-400/80">
                      ✓ above floor
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
            <input
              type="checkbox"
              checked={autoYearly}
              onChange={(e) => setAutoYearly(e.target.checked)}
              className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
            />
            Auto-set yearly price = monthly × 12
          </label>

          {/* Live commission preview */}
          {monthlyNum > 0 && (
            <div className="rounded-lg bg-brand-subtle dark:bg-brand-dm-subtle/30 border border-brand-border dark:border-brand-dm-border p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-brand dark:text-brand-dm mb-2">
                What you'll earn per customer · per month
              </div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400">Customer pays you</div>
                  <Money minor={monthlyNum * 100} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50 font-semibold block mt-0.5" />
                </div>
                <div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400">Platform takes ({r.commissionPct}%)</div>
                  <Money minor={platformCut * 100} hideCents tone="neutral" className="text-zinc-700 dark:text-zinc-200 font-medium block mt-0.5" />
                </div>
                <div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400">You earn ({100 - r.commissionPct}%)</div>
                  <Money minor={yourEarnings * 100} hideCents tone="neutral" className="text-emerald-700 dark:text-emerald-300 font-semibold block mt-0.5" />
                </div>
              </div>
            </div>
          )}

          <fieldset className="border-t border-zinc-200 dark:border-zinc-700 pt-5">
            <legend className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-3">
              Resource limits enforced on the POS
            </legend>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Locations" type="number" min={0} value={locations} onChange={(e) => setLocations(e.target.value)} />
              <Input label="Users" type="number" min={0} value={users} onChange={(e) => setUsers(e.target.value)} />
              <div>
                <Input
                  label="Products"
                  type="number"
                  min={0}
                  value={products}
                  onChange={(e) => setProducts(e.target.value)}
                  disabled={unlimitedProducts}
                />
                <label className="mt-1.5 flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={unlimitedProducts}
                    onChange={(e) => setUnlimitedProducts(e.target.checked)}
                    className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                  />
                  Unlimited
                </label>
              </div>
              <div>
                <Input
                  label="Invoices / month"
                  type="number"
                  min={0}
                  value={invoices}
                  onChange={(e) => setInvoices(e.target.value)}
                  disabled={unlimitedInvoices}
                />
                <label className="mt-1.5 flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={unlimitedInvoices}
                    onChange={(e) => setUnlimitedInvoices(e.target.checked)}
                    className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                  />
                  Unlimited
                </label>
              </div>
            </div>
          </fieldset>

          <fieldset className="border-t border-zinc-200 dark:border-zinc-700 pt-5">
            <legend className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
              Initial status
            </legend>
            <div className="space-y-2">
              <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
                <input
                  type="radio"
                  name="rs-status"
                  checked={status === 'draft'}
                  onChange={() => setStatus('draft')}
                  className="text-brand focus:ring-brand"
                />
                <span><strong>Draft</strong> — invisible to customers; finish setup before publishing</span>
              </label>
              <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
                <input
                  type="radio"
                  name="rs-status"
                  checked={status === 'published'}
                  onChange={() => setStatus('published')}
                  className="text-brand focus:ring-brand"
                />
                <span><strong>Published</strong> — live at <span className="mono">{r.slug}.itcitysoft.com</span> immediately</span>
              </label>
            </div>
          </fieldset>
        </div>
      )}
    </Drawer>
  );
}

function StatTile({
  label,
  value,
  subtitle,
}: {
  label: string;
  value: React.ReactNode;
  subtitle: string;
}) {
  return (
    <Card className="p-4 sm:p-5">
      <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{label}</div>
      <div className="mt-1.5 text-xl sm:text-2xl font-semibold text-zinc-950 dark:text-zinc-50">{value}</div>
      <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{subtitle}</div>
    </Card>
  );
}

function FilterDropdown({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ v: string; l: string }>;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
      >
        <span className="text-zinc-500 dark:text-zinc-400">{label}:</span>
        <span>{value}</span>
        <ChevronDown className="w-3 h-3 text-zinc-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-1 left-0 z-20 min-w-[180px] rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-900 shadow-md py-1">
            {options.map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => {
                  onChange(o.v);
                  setOpen(false);
                }}
                className={cn(
                  'block w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-50 dark:hover:bg-zinc-800',
                  'text-zinc-700 dark:text-zinc-200',
                )}
              >
                {o.l}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
